import React, { useEffect, useRef } from "react";
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js';
import {Bar, Line} from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import {Box, Button, Typography} from "@mui/material";
import {useParams} from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchBufferedState, fetchSiteData } from "../../scripts/api_v2/commands_client";
import { useQuery } from "@tanstack/react-query";
import { useSiteData } from "hooks/use_site";
import { applyAutoScale } from "./chart_options";

ChartJS.register(
  TimeScale,
  LinearScale,
  CategoryScale,
  LineController,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);


// Ensures the y-axis label gutter is at least this wide on every stacked chart,
// so vertically stacked charts with differently-sized labels (e.g. "-35.000.000" vs "-1,0")
// still line up their plot areas.
const MIN_Y_AXIS_WIDTH = 100;


export const getLiveChartOptions = () => ({
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  plugins: {
    legend: {
      position: "bottom",
      display: true,
    },
  },
  scales: {
      y: {
          afterFit: (scale) => {
              scale.width = Math.max(scale.width, MIN_Y_AXIS_WIDTH);
          },
      },
  },
})

export function LiveControlChart() {
    const params = useParams();
    const backend = useBackend();

    const [paused, setPaused] = React.useState(false);
    const pausedRef = React.useRef(false);
    const since = React.useRef(0);
    const labelsRef = React.useRef([])
    const dataRef = React.useRef({
        "grid": [],
        "grid_setpoint": [],
        "grid_target": [],
        "flex": [],
        'flex_min': [],
        'flex_max': [],
        'production_min': [],
        'production_max': [],
        'flex_target': [],
        "flex_setpoint": [],
        "production": [],
        "production_setpoint": [],
        "production_target": [],
        "controllable_production": [],
        "curtailable_production": [],
        "uncontrollable_production": [],
        "current_production": [],
        "current_curtailable_production": [],
        "current_uncurtailable_production": [],
        "total_production": [],
        "total_curtailable_production": [],
        "total_uncurtailable_production": [],
        "total_controllable_production": [],
        "uncurtailable_production": [],
        "reactive_flex": [],
        'reactive_flex_target': [],
        "reactive_flex_setpoint": [],
        "reactive_grid": [],
        "reactive_min": [],
        "reactive_max": [],
        "reactive_grid_target": [],
        "reactive_grid_setpoint": [],
        "reactive_grid_min": [],
        "reactive_grid_max": [],
        "grid_min": [],
        "grid_max": [],
    })

    const [labels, setLabels] = React.useState(labelsRef.current)
    const numberOfPoints = 180 * 2 // only show three minutes of data
    const [data, setData] = React.useState(dataRef.current)

    const {data: newDataPoint, isFetching, error} = useQuery({
            queryKey: [REACT_QUERY_KEYS.CONTROL_LOOP_BUFFERED_STATE, backend.fetch, params.site_id],
            queryFn: async () => {
                try {
                    return await fetchBufferedState(backend.fetch, params.site_id, since.current)
                } catch (error) {
                    console.error("Error in query function:", error);
                    throw error; // Rethrow to let react-query handle it
                }
            },
            refetchInterval: 1000,
            enabled: !!params.site_id && !pausedRef.current,
        }
    );

    useEffect(() => {
        // There is no onSuccess for useQuery...
        if (newDataPoint != null) {
            for (let i = 0; i < newDataPoint.length; i++) {
                const new_ts = newDataPoint[i][0];
                since.current = new_ts;
                labelsRef.current = [...labelsRef.current, new Date(1000 * new_ts)].slice(-numberOfPoints);

                Object.entries(newDataPoint[i][1]).forEach(([key, value]) => {
                    if (key in dataRef.current) {
                        dataRef.current[key] = [...dataRef.current[key], value].slice(-numberOfPoints);
                    }
                });
            }
            setLabels(labelsRef.current);
            setData(dataRef.current);
        }
    }, [newDataPoint]);

    return !error ? <Box ml={1} display="flex" flexDirection="column" height="100%" width="100%">
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, mt: 1}}>Active Power</Typography>
            <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between">
                <LiveGridChart labels={labels} data={data}/>
                <LiveCurtailmentChart labels={labels} data={data}/>
            </Box>
            <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between">
                <LiveFlexChart labels={labels} data={data}/>
            </Box>

            <Typography variant="h6" fontWeight="bold" sx={{mb: 1, mt: 6}}>Reactive Power</Typography>
            <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between">
                <LiveReactiveGridChart labels={labels} data={data}/>
                <LiveReactiveFlexChart labels={labels} data={data}/>
            </Box>
            <Button sx={{m: 1, width: '200px', alignSelf: 'center'}} onClick={() => {
                setPaused(!paused);
                pausedRef.current = !pausedRef.current
            }}>{paused ? 'Unpause' : 'Pause'}</Button>
        </Box> :
        <div>Error loading data</div>

}


const LiveFlexChart = (
    {
        labels, data
    }
) => {
    const params = useParams();
    const backend = useBackend();

    let {data: siteData, error, isLoading, refetch} = useQuery({
        queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
        queryFn: () => fetchSiteData(backend.fetch, params.site_id),
        enabled: false // sitePage will refresh the query
    });

    const options = getLiveChartOptions()
    options.scales.y.suggestedMin = -siteData?.charge_rate * 1.1;
    options.scales.y.suggestedMax = siteData?.charge_rate * 1.1;
    applyAutoScale(options, [data.flex, data.flex_setpoint, data.flex_target, data.flex_min, data.flex_max], "W");

    options.scales.x = {
        type: 'time',
        time: {
            unit: 'minute',
            displayFormats: {
                minute: 'HH:mm'
            }
        },
    }

    const flexData = {
        labels: labels,
        datasets: [
            {
                fill: false,
                label: 'Flex',
                data: data.flex?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(255, 153, 0, 1)',
                backgroundColor: 'rgba(255, 153, 0, 1)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Flex setpoint',
                data: data.flex_setpoint?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(100, 100, 100, 0.5)',
                backgroundColor: 'rgba(100, 100, 100, 0.5)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Flex target',
                data: data.flex_target?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.8)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'start',
                label: 'Minimum Flex',
                data: data.flex_min?.map(el => el ? Math.round(el) : el),
                borderWidth: 0,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.05)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'end',
                label: 'Maximum Flex',
                data: data.flex_max?.map(el => el ? Math.round(el) : el),
                borderWidth: 0,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.05)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            },
        ]
    }

    return <Box height="330px" width="calc(50% - 25px)"><Line data={flexData} width={"100%"}
                                                              options={options}/></Box>
}

const LiveGridChart = (
    {
        labels, data
    }
) => {
    const params = useParams();
    const backend = useBackend();

    let {data: siteData, error, isLoading, refetch} = useQuery({
        queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
        queryFn: () => fetchSiteData(backend.fetch, params.site_id),
        enabled: false // sitePage will refresh the query
    });
    const options = getLiveChartOptions()
    options.scales.y.suggestedMin = -siteData?.config?.max_grid_inject_sp * 1.1;
    options.scales.y.suggestedMax = siteData?.config?.max_grid_offtake_sp * 1.1;
    applyAutoScale(options, [data.grid, data.grid_setpoint, data.grid_target, data.grid_min, data.grid_max], "W");

    options.scales.x = {
        type: 'time',
        time: {
            unit: 'minute',
            displayFormats: {
                minute: 'HH:mm'
            }
        },
    }

    const gridData = {
        labels: labels,
        datasets: [
            {
                fill: false,
                label: 'Grid',
                data: data.grid?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(102, 0, 205, 1)',
                backgroundColor: 'rgba(102, 0, 205, 1)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Grid setpoint',
                data: data.grid_setpoint?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(100, 100, 100, 0.5)',
                backgroundColor: 'rgba(100, 100, 100, 0.5)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Grid target',
                data: data.grid_target?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.8)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'end',
                label: 'Maximum',
                data: data.grid_max?.map(el => el ? Math.round(el) : el),
                borderWidth: 0,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.5)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'start',
                label: 'Minimum',
                data: data.grid_min?.map(el => el ? Math.round(el) : el),
                borderWidth: 0,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.5)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            }
        ],
    }
    return <Box height="330px" width="calc(50% - 25px)"><Line data={gridData} width={"100%"}
                                                              options={options}/></Box>
}

const LiveCurtailmentChart = (
    {
        labels, data
    }
) => {

    let {data: siteData, error, isLoading, refetch} = useSiteData();

    const curtailableValues = data.current_curtailable_production ?? data.curtailable_production ?? data.controllable_production ?? data.production ?? [];
    const uncurtailableValues = data.current_uncurtailable_production ?? data.uncurtailable_production ?? data.uncontrollable_production ?? [];

    const maxTotal = curtailableValues.reduce((acc, el, i) => {
        const curtailable = el ?? 0;
        const uncurtailable = uncurtailableValues?.[i] ?? 0;
        return Math.max(acc, curtailable + uncurtailable);
    }, 0) ?? 0;

    const maxLineTotal = (data.production_max ?? []).reduce((acc, el) => Math.max(acc, el ?? 0), 0);

    const options = getLiveChartOptions()
    options.animation = {duration: 0};
    options.transitions = {
        show: {
            animations: {
                colors: {type: 'color', from: 'transparent', duration: 400},
                visible: {type: 'boolean', duration: 400}
            }
        },
        hide: {
            animations: {
                colors: {type: 'color', to: 'transparent', duration: 400},
                visible: {type: 'boolean', duration: 400, easing: 'linear'}
            }
        }
    };
    options.scales.y.suggestedMin = 0;
    options.scales.y.suggestedMax = Math.max(maxTotal, maxLineTotal, siteData?.config?.max_grid_offtake_sp ?? 0) * 1.2;
    options.scales.y.stacked = true;
    applyAutoScale(options, [curtailableValues, uncurtailableValues, data.production_max, data.production_min], "W");

    options.plugins.tooltip = {
        callbacks: {
            label: (context) => {
                const dataset = context.chart.data.datasets[context.datasetIndex];
                return `${dataset.label}: ${context.parsed.y}`;
            }
        }
    };

    options.scales.x = {
        type: 'time',
        time: {
            unit: 'minute',
            displayFormats: {
                minute: 'HH:mm'
            }
        },
    }

    const gridData = {
        labels: labels,
        datasets: [
            {
                fill: 'origin',
                label: 'Uncontrollable production',
                data: uncurtailableValues.map(el => el ? Math.round(el) : el),
                stack: 'production',
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                backgroundColor: 'rgba(70, 70, 70, 0.25)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Controllable production',
                data: curtailableValues.map(el => el ? Math.round(el) : el),
                stack: 'production',
                borderWidth: 2,
                cubicInterpolationMode: "monotone",
                borderColor: "rgba(0, 204, 0, 1)",
                backgroundColor: "rgba(0, 204, 0, 1)",
                pointStyle: false,
                type: "line",
            },
            {
                fill: false,
                label: 'Production setpoint',
                data: data.production_setpoint?.map(el => el ? Math.round(el) : el),
                stack: 'setpoint',
                borderWidth: 2,
                order: 1,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(100, 100, 100, 0.5)',
                backgroundColor: 'rgba(100, 100, 100, 0.5)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Production target',
                data: data.production_target?.map(el => el ? Math.round(el) : el),
                stack: 'target',
                borderWidth: 2,
                order: 1,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.8)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'end',
                label: 'Maximum',
                data: data.production_max?.map(el => el ? Math.round(el) : el),
                stack: 'max',
                borderWidth: 0,
                order: -1,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.8)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'start',
                label: 'Minimum',
                data: data.production_min?.map(el => el ? Math.round(el) : el),
                stack: 'min',
                borderWidth: 0,
                order: -1,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.8)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            },
        ],
    }
    return <Box height="330px" width="calc(50% - 25px)"><Line data={gridData} width={"100%"}
                                                              options={options}/></Box>
}

const LiveReactiveFlexChart = (
    {
        labels, data
    }
) => {
    const options = getLiveChartOptions()
    let {data: siteData, error, isLoading, refetch} = useSiteData();

    options.scales.x = {
        type: 'time',
        time: {
            unit: 'minute',
            displayFormats: {
                minute: 'HH:mm'
            }
        },
    }

    options.scales.y.suggestedMin = -siteData?.charge_rate * 1.1;
    options.scales.y.suggestedMax = siteData?.charge_rate * 1.1;
    applyAutoScale(options, [data.reactive_flex, data.reactive_flex_setpoint, data.reactive_flex_target, data.reactive_min, data.reactive_max], "VAr");

    const gridData = {
        labels: labels,
        datasets: [
            {
                fill: false,
                label: 'Reactive power',
                data: data.reactive_flex?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: '#03cafc',
                backgroundColor: '#03cafc',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Reactive setpoint',
                data: data.reactive_flex_setpoint?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(100, 100, 100, 0.5)',
                backgroundColor: 'rgba(100, 100, 100, 0.5)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Reactive target',
                data: data.reactive_flex_target?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.8)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'end',
                label: 'Maximum',
                data: data.reactive_max?.map(el => el ? Math.round(el) : el),
                borderWidth: 0,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.5)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'start',
                label: 'Minimum',
                data: data.reactive_min?.map(el => el ? Math.round(el) : el),
                borderWidth: 0,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.5)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            }
        ],
    }
    return <Box height="330px" width="calc(50% - 25px)"><Line data={gridData} width={"100%"}
                                                              options={options}/></Box>
}

const LiveReactiveGridChart = (
    {
        labels, data
    }
) => {
    const options = getLiveChartOptions()
    let {data: siteData, error, isLoading, refetch} = useSiteData();

    options.scales.x = {
        type: 'time',
        time: {
            unit: 'minute',
            displayFormats: {
                minute: 'HH:mm'
            }
        },
    }

    options.scales.y.suggestedMin = -siteData?.config?.max_grid_inject_sp * 1.1;
    options.scales.y.suggestedMax = siteData?.config?.max_grid_offtake_sp * 1.1;
    applyAutoScale(options, [data.reactive_grid, data.reactive_grid_setpoint, data.reactive_grid_target, data.reactive_grid_min, data.reactive_grid_max], "VAr");

    const gridData = {
        labels: labels,
        datasets: [
            {
                fill: false,
                label: 'Reactive grid',
                data: data.reactive_grid?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: '#1878f5',
                backgroundColor: '#1878f5',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Reactive setpoint',
                data: data.reactive_grid_setpoint?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(100, 100, 100, 0.5)',
                backgroundColor: 'rgba(100, 100, 100, 0.5)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: false,
                label: 'Reactive target',
                data: data.reactive_grid_target?.map(el => el ? Math.round(el) : el),
                borderWidth: 2,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.8)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'end',
                label: 'Maximum',
                data: data.reactive_grid_max?.map(el => el ? Math.round(el) : el),
                borderWidth: 0,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.5)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            },
            {
                fill: 'start',
                label: 'Minimum',
                data: data.reactive_grid_min?.map(el => el ? Math.round(el) : el),
                borderWidth: 0,
                cubicInterpolationMode: 'monotone',
                borderColor: 'rgba(0, 0, 0, 0.5)',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                pointStyle: false,
                type: 'line'
            }
        ],
    }
    return <Box height="330px" width="calc(50% - 25px)"><Line data={gridData} width={"100%"}
                                                              options={options}/></Box>
}
